<template>
  <fts-dropdown-select
    append-to-body
    prevent-overflow-alt-axis
    prevent-overflow-tether-off
    :offset="[-8, -47]"
    follow-width
    content-class="dropdown-menu"
    overlay
    class="fts-information-design-shortcut"
    @content:keydown-enter="optionPointerSelect"
    @content:keydown-up="optionPointerUp"
    @content:keydown-down="optionPointerDown"
    ref="dropdownSelect"
    @show="onShow"
    @hide="onHide"
  >
    <div slot="toggle" class="information-design-shorcut-toggle">
      Type to add new information <fts-release-badge dev class="fts-align-top" />
    </div>

    <template #menu="{ select }">
      <div class="dropdown-filter" :class="{'dropdown-filter--searching': searching}">
        <div class="fts-form-control-filter">
          <input
            class="fts-form-control"
            v-model.trim="filterKeyword"
            placeholder="Type to add new information"
            @input="onSearch"
            @keydown.up.prevent
            @keydown.down.prevent
            ref="filterKeywordInput"
          />
          <div class="search-icon">
            <fts-spinner :size="14" show v-if="searching" />
            <!-- <font-awesome-icon :icon="['fas', 'search']" fixed-width v-else /> -->
          </div>
        </div>
      </div>

      <div class="dropdown-message dropdown-message--error" v-if="designError">
        {{designError}}
      </div>

      <template v-else-if="getOptions">
        <div
          v-for="(item, index) in getOptions"
          :key="index"
          class="dropdown-option"
          :class="[
            {'dropdown-option--active': focusIndex === index},
            {'dropdown-option--disabled': item.type == 'exists'}
          ]"
          @click="onSelect(item)"
        >
          <template v-if="item.type == 'exists'">
            <fts-label-group
              :item="item.value"
              information 
            />&nbsp;
            <fts-badge size="sm" color="text">
              In Used
            </fts-badge>
          </template>

          <template v-else-if="item.type == 'suggest'">
            <fts-label-group
              :item="item.value"
              emoji
              information
              :informationModules="modules"
              class="suggestion"
            />&nbsp;
            <fts-badge size="sm" color="ai">
              <font-awesome-icon :icon="['fas', 'wand-sparkles']" /> AI
            </fts-badge>
          </template>

          <template v-else-if="item.value && item.value.displayName">
            <span class="fts-text-default" style="white-space: nowrap;">Create&nbsp;</span>
            <span style="white-space: nowrap;text-overflow: ellipsis;overflow: hidden;">{{item.value.displayName}}</span>
          </template>

          <span class="fts-text-default" v-else>Add new information</span>

          <div class="dropdown-option-kbd-hint" v-if="item.type != 'exists' && focusIndex === index">
            <kbd>Enter</kbd> to create
          </div>
        </div>
      </template>
    </template>
  </fts-dropdown-select>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import commons from '../commons/commons';
import dropdown from '../commons/dropdown';
import designValidator from '../commons/designValidator';

import FTSBadge from '../elements/FTSBadge';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';

export default {
  components: {
    "fts-badge": FTSBadge,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-release-badge": FTSReleaseBadge,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy
  },
  props: {
    modules: Array,
    information: Array
  },
  data: () => ({
    filterKeyword: null,
    searching: false,
    suggestion: [],
    focusIndex: 0,
    designError: null
  }),
  methods: {
    onShow() {
      this.$nextTick(() => {
        this.$refs.filterKeywordInput.focus();
      });
    },
    onHide() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async onSearch() {
      this.suggestion = [];
      this.designError = false;

      if (!this.exists && this.filterKeyword) {
        this.search(this.filterKeyword, this);
      }
    },
    search: _.debounce(async (search, vm) => {
      if (!search) return;

      const informationReservedKeywords = designValidator.informationReservedKeywords.concat(["username"]);
      if (!designValidator.isInformationNonReserved(search, informationReservedKeywords)) {
        vm.designError = designValidator.errorMessage.informationNameReserved(informationReservedKeywords);
        return;
      } 
      
      if (!designValidator.isInformationFormatValid(search, designValidator.informationFormatRestriction)) {
        vm.designError = designValidator.errorMessage.informationNameFormat(designValidator.informationFormatRestriction);
        return;
      }

      vm.searching = true;

      const findModule = Array.isArray(vm.modules) && vm.modules.find(x =>
        x.name && (x.name.toLowerCase().trim() == search.toLowerCase())
      );

      if (findModule) {
        vm.suggestion = [{
          type: "suggest",
          value: {
            connection: findModule.name,
            connectionId: findModule.id,
            displayName: search, 
            type: findModule.isUserCollection ?  20 : 7 // to-do isUserCollection
          }
        }];
      } else {
        let informationTypes = [0];
        try {
          const res = await canvasDesignStore.dispatch("ai", {
            type: "information",
            text: search
          });

          informationTypes = (commons.isArray(res.data) ? [...res.data] : [res.data])
            .filter(x => {
              if (!commons.isInteger(x)) {
                return false;
              }

              const findInformationType = commons.informationTypes.find(y => y.id == x);
              if (findInformationType) {
                if (findInformationType.isAlpha) {
                  return authorizationStore.getters.isInternal;
                } else if (findInformationType.isBeta) {
                  return authorizationStore.getters.isBeta;
                }
                return true;
              }
              return false;
            })
          ;

          if (!informationTypes.length) {
            informationTypes = [0];
          }
        } catch(err) {
          console.error(err);
        } finally {
          vm.suggestion = informationTypes.map(x => ({
            type: "suggest",
            value: {
              displayName: search,
              type: x
            }
          }));

          vm.suggestion.push({
            type: "default",
            value: {
              displayName: search
            }
          });

          vm.focusIndex = 0;
        }
      }
      
      vm.searching = false;
    }, 800),
    onSelect({ type, value }) {
      let formData = {...value};
      let requiresSetting = false;

      if (
        !formData.displayName ||
        formData.type == null ||
        formData.type == undefined ||
        [7, 8, 14, 15, 20, 21, 27, 28].indexOf(formData.type) > -1
      ) {
        requiresSetting = true;
      }

      if (type == 'exists') return;

      if (requiresSetting) {
        this.$refs.dropdownSelect.select();
      } else {
        this.filterKeyword = null;
        this.suggestion = [];
        this.$refs.filterKeywordInput.focus();

        this.$nextTick(() => {
          this.$refs.dropdownSelect.$refs.popper.popperInstance.update();
        });
      }

      this.$emit("option:create", { formData, requiresSetting });
    },
    optionPointerSelect() {
      const focusOption = this.getOptions[this.focusIndex];
      if (this.focusIndex > -1 && focusOption) {
        this.onSelect(focusOption);
      }
    },
    optionPointerUp(item) {
      this.focusIndex = dropdown.optionPointerUp({
        currentIndex: this.focusIndex,
        options: this.getOptions,
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    },
    optionPointerDown(item) {
      this.focusIndex = dropdown.optionPointerDown({
        currentIndex: this.focusIndex,
        options: this.getOptions,
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    }
  },
  computed: {
    exists() {
      if (this.filterKeyword && Array.isArray(this.information)) {
        return this.information.find(x =>
          x.displayName && (x.displayName.toLowerCase().trim() == this.filterKeyword.toLowerCase().trim())
        );
      }
      return null;
    },
    getOptions() {
      if (this.exists) {
        let options = [{ type: "exists", value: this.exists }];
        this.filterKeyword && options.push({ type: "default" }); 
        return options;
      } else {
        return this.suggestion;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-information-design-shortcut {
  padding: 0.5rem 0.5rem;
  .information-design-shorcut-toggle {
    padding: 0.375rem 0.75rem;
    border: 1px solid transparent;
    border-radius: $border-radius;
    font-size: $font-size;
    line-height: $line-height;
    color: var(--fts-form-control-placeholder);
    &:hover {
      background-color: var(--fts-gray-100);
    }
  }
}
</style>